import { useState, useEffect, useRef } from 'react'
import { profileUrl } from '../../api/tmdb'
import { updateParty } from '../../utils/partyGame'
import PartyTrilogyStandings from './PartyTrilogyStandings'
import PartyTrilogyFinal from './PartyTrilogyFinal'
import { IconArrowRight, IconClock, IconCheck, IconX } from '../icons'

const TIMEOUT_MS  = 5 * 60 * 1000  // 5 minutes per round
const DNF_HOPS    = 10
const DNF_TIME_MS = 300_000         // 5 min in ms

function fmtTime(secs) {
  if (secs == null) return '--:--'
  const m = String(Math.floor(secs / 60)).padStart(2, '0')
  const s = String(secs % 60).padStart(2, '0')
  return `${m}:${s}`
}

/**
 * Persist round results + cumulative scores to Firestore, then advance status
 * to 'round_standings'. Also saves the round's puzzle actors into rounds[N]
 * so the final breakdown can reference them.
 *
 * isTimeout=true → mark any still-playing players as DNF.
 */
async function finalizeRound(party, roundNum, isTimeout) {
  const players        = party.players ?? {}
  const updates        = {}
  const roundResults   = {}
  const prevCumulative = party.cumulativeScores ?? {}
  const newCumulative  = {}

  for (const [uid, p] of Object.entries(players)) {
    const finished = p.status === 'finished'

    if (isTimeout && !finished && p.status !== 'dnf') {
      updates[`players.${uid}.status`] = 'dnf'
    }

    const hops   = finished ? (p.hops  ?? DNF_HOPS)               : DNF_HOPS
    const timeMs = finished ? ((p.time ?? DNF_TIME_MS / 1000) * 1000) : DNF_TIME_MS

    roundResults[uid] = { hops, timeMs, completed: finished }

    const prev = prevCumulative[uid] ?? { totalHops: 0, totalTimeMs: 0 }
    newCumulative[uid] = {
      totalHops:   prev.totalHops   + hops,
      totalTimeMs: prev.totalTimeMs + timeMs,
    }
  }

  // Save per-round results + puzzle actors (needed for final breakdown)
  updates[`rounds.${roundNum}.results`]    = roundResults
  updates[`rounds.${roundNum}.startActor`] = party.startActor ?? null
  updates[`rounds.${roundNum}.goalActor`]  = party.goalActor  ?? null
  updates.cumulativeScores                 = newCumulative
  updates.status                           = 'round_standings'

  await updateParty(party.id, updates)
}

function ActorThumb({ actor, label }) {
  if (!actor) return null
  const initial = (actor.name || 'A').charAt(0).toUpperCase()
  return (
    <div className="party-battle-screen__actor-thumb">
      <div className="party-battle-screen__actor-img">
        {actor.profile_path
          ? <img src={profileUrl(actor.profile_path, 'w185')} alt={actor.name} />
          : <span className="party-battle-screen__actor-initial">{initial}</span>}
      </div>
      <span className="party-battle-screen__actor-label">{label}</span>
      <span className="party-battle-screen__actor-name">{actor.name}</span>
    </div>
  )
}

/**
 * Host-side Trilogy game screen — orchestrates three sub-statuses:
 *
 *   trilogy-playing  → live player board; auto-finalizes on all-done or timeout
 *   round_standings  → delegates to PartyTrilogyStandings (isHost=true)
 *   final_standings  → delegates to PartyTrilogyFinal (isHost=true)
 *
 * Props
 *   party         — live Firestore party doc
 *   onNextRound   — App.jsx callback: bump currentRound, clear player state, assign pickers
 *   onPlayAgain   — App.jsx callback: reset party fully, return to mode select
 *   onEndParty    — App.jsx callback: mark ended, clean up, return to multiplayer
 *   readOnly      — render the live board only (no host finalize writes, no
 *                   advance buttons). Used by finished players to watch the round.
 */
export default function PartyTrilogyGameplay({ party, onNextRound, onPlayAgain, onEndParty, readOnly = false }) {
  const status       = party?.status
  const currentRound = party?.currentRound ?? 1
  const players      = party?.players ?? {}
  const playerCount  = Object.keys(players).length

  const roundFiredRef = useRef(false)
  const [elapsed, setElapsed] = useState(0)

  // ── Timer ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (status !== 'trilogy-playing') return
    const startedAt = party?.playingStartedAt
    if (!startedAt) return
    const startMs = startedAt?.toMillis?.() ?? (startedAt?.seconds ? startedAt.seconds * 1000 : Date.now())
    const tick = () => setElapsed(Math.floor((Date.now() - startMs) / 1000))
    tick()
    const id = setInterval(tick, 1000)
    return () => clearInterval(id)
  }, [party?.playingStartedAt, status])

  // Reset the guard when a new round begins
  useEffect(() => {
    if (status === 'trilogy-playing') roundFiredRef.current = false
  }, [status, currentRound])

  // ── Auto-finalize: all players done ────────────────────────────────────────
  useEffect(() => {
    if (readOnly || status !== 'trilogy-playing' || roundFiredRef.current || playerCount === 0) return
    const done = Object.values(players).filter(
      (p) => p.status === 'finished' || p.status === 'dnf'
    ).length
    if (done < playerCount) return
    roundFiredRef.current = true
    finalizeRound(party, currentRound, false).catch(() => {})
  }, [status, players, playerCount, currentRound, party, readOnly])

  // ── Auto-finalize: 5-minute timeout ────────────────────────────────────────
  useEffect(() => {
    if (readOnly || status !== 'trilogy-playing' || roundFiredRef.current) return
    const startedAt = party?.playingStartedAt
    if (!startedAt) return
    const startMs   = startedAt?.toMillis?.() ?? (startedAt?.seconds ? startedAt.seconds * 1000 : Date.now())
    const remaining = TIMEOUT_MS - (Date.now() - startMs)
    if (remaining <= 0) {
      if (!roundFiredRef.current) {
        roundFiredRef.current = true
        finalizeRound(party, currentRound, true).catch(() => {})
      }
      return
    }
    const id = setTimeout(() => {
      if (roundFiredRef.current) return
      roundFiredRef.current = true
      finalizeRound(party, currentRound, true).catch(() => {})
    }, remaining)
    return () => clearTimeout(id)
  }, [party?.playingStartedAt, status, currentRound, party, readOnly])

  // ── Delegate: round standings ───────────────────────────────────────────────
  if (status === 'round_standings') {
    return (
      <PartyTrilogyStandings
        party={party}
        isHost={!readOnly}
        onNextRound={onNextRound}
        onFinalResults={async () => {
          await updateParty(party.id, { status: 'final_standings' })
        }}
      />
    )
  }

  // ── Delegate: final standings ───────────────────────────────────────────────
  if (status === 'final_standings') {
    return (
      <PartyTrilogyFinal
        party={party}
        isHost
        onPlayAgain={onPlayAgain}
        onDone={onEndParty}
      />
    )
  }

  // ── Live playing board ──────────────────────────────────────────────────────
  if (status === 'trilogy-playing') {
    const finished     = Object.entries(players).filter(([, p]) => p.status === 'finished')
    const stillPlaying = Object.entries(players).filter(([, p]) => p.status === 'playing' || !p.status)
    const dnf          = Object.entries(players).filter(([, p]) => p.status === 'dnf')
    const board        = [
      ...finished.sort((a, b) => {
        const diff = (a[1].hops ?? 999) - (b[1].hops ?? 999)
        if (diff !== 0) return diff
        return (a[1].time ?? 9999) - (b[1].time ?? 9999)
      }),
      ...stillPlaying,
      ...dnf,
    ]
    const finishedCount = finished.length + dnf.length

    return (
      <div className="party-battle-screen">
        <div className="party-battle-screen__header">
          {/* Round badge */}
          <span style={{
            fontSize: '0.78rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--accent-gold)',
          }}>
            Round {currentRound} of 3
          </span>

          <div className="party-battle-screen__actors">
            <ActorThumb actor={party?.startActor} label="Start" />
            <span className="party-battle-screen__arrow" aria-hidden="true">
              <IconArrowRight size={20} />
            </span>
            <ActorThumb actor={party?.goalActor}  label="Goal" />
          </div>

          <div className="party-battle-screen__timer">
            <span className="party-battle-screen__timer-label" aria-hidden="true">
              <IconClock size={20} />
            </span>
            <span className="party-battle-screen__timer-value">{fmtTime(elapsed)}</span>
          </div>

          <p className="party-battle-screen__progress">
            {finishedCount}/{playerCount} finished
          </p>
        </div>

        <div className="party-battle-screen__leaderboard">
          {board.map(([uid, p]) => {
            const initial = (p.username || 'G').charAt(0).toUpperCase()
            const rowClass = [
              'party-battle-row',
              p.status === 'finished' ? 'party-battle-row--finished' : '',
              p.status === 'dnf' ? 'party-battle-row--dnf' : '',
            ].join(' ').trim()
            return (
              <div key={uid} className={rowClass}>
                <div className="party-battle-row__avatar">
                  {p.avatar
                    ? <img src={p.avatar} alt="" />
                    : <span>{initial}</span>}
                </div>
                <span className="party-battle-row__name">{p.username || 'Guest'}</span>
                <span className="party-battle-row__status">
                  {p.status === 'finished' ? (
                    <span className="pb-status-chip pb-status-chip--finished">
                      <IconCheck size={12} aria-hidden="true" />
                      <span>{p.hops} hop{p.hops !== 1 ? 's' : ''} · {fmtTime(p.time)}</span>
                    </span>
                  ) : p.status === 'dnf' ? (
                    <span className="pb-status-chip pb-status-chip--dnf">
                      <IconX size={12} aria-hidden="true" />
                      <span>DNF</span>
                    </span>
                  ) : (
                    <span className="pb-status-chip pb-status-chip--playing">
                      <span className="pb-shoot-dot" aria-hidden="true" />
                      <span>Racing</span>
                    </span>
                  )}
                </span>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  // Transitional fallback
  return (
    <div className="party-battle-screen">
      <p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: 32 }}>
        Loading round {currentRound}...
      </p>
    </div>
  )
}
